import React, { useMemo } from "react";
import { Breadcrumbs, Link } from "@mui/material";
import { useHistory } from "react-router-dom";

type Category = {
    id: number;
    name: string;
    slug: string;
    children: Category[];
};

type Props = {
    slug: string;
    categories: Category[];
};

const CategoryBreadcrumbs = ({ slug, categories }: Props) => {
    const history = useHistory();

    const flatMap = useMemo(() => {
        const map = new Map<string, Category>();

        const walk = (nodes: Category[]) => {
            for (const n of nodes) {
                map.set(n.slug, n);
                if (n.children?.length) walk(n.children);
            }
        };

        walk(categories);
        return map;
    }, [categories]);

    const parts = useMemo(() => {
        if (!slug) return [];

        const segments = slug.split("/");
        let current = "";

        return segments.map((part) => {
            current = current ? `${current}/${part}` : part;

            return {
                slug: current,
                category: flatMap.get(current),
            };
        });
    }, [slug, flatMap]);

    if (!slug) return null;

    return (
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <span>Categories</span>

            {parts.map((item, index) => {
                const isLast = index === parts.length - 1;

                if (isLast) {
                    return <span>{item.category?.name ?? item.slug}</span>;
                }

                return (
                    <Link
                        key={item.slug}
                        underline="hover"
                        color="inherit"
                        onClick={() => history.push(`/category/${item.slug}`)}
                        sx={{ cursor: "pointer" }}
                    >
                        {item.category?.name ?? item.slug}
                    </Link>
                );
            })}
        </Breadcrumbs>
    );
};

export default CategoryBreadcrumbs;
